import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useMemo } from 'react';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import useTranslate from 'hooks/useTranslate';
import type { ServiceProviderAdminRow, SupportEmailFormInterface } from 'types';
import { buildSupportEmailValidationSchema } from 'utils/validations/adminPanel';

interface SupportEmailFormProps {
    provider: ServiceProviderAdminRow;
    loading?: boolean;
    onClose: () => void;
    onSubmit: (values: SupportEmailFormInterface) => void | Promise<void>;
}

export function SupportEmailForm({
    provider,
    loading = false,
    onClose,
    onSubmit,
}: SupportEmailFormProps) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { translate } = useTranslate();
    const isNew = !provider.support_email;
    const validationSchema = useMemo(() => buildSupportEmailValidationSchema(isNew), [isNew]);

    const initialValues: SupportEmailFormInterface = {
        support_email: provider.support_email ?? '',
        support_email_password: '',
    };

    return (
        <Dialog
            open
            onClose={loading ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            fullScreen={fullScreen}
            scroll="paper"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: fullScreen ? 0 : 2,
                        maxHeight: fullScreen ? '100%' : '90vh',
                    },
                },
            }}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                <Form
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    <DialogTitle sx={{ flexShrink: 0, py: 2 }}>
                        <Typography
                            variant="h6"
                            component="span"
                            sx={{ fontWeight: 700, display: 'block' }}
                        >
                            {translate(isNew ? 'setSupportEmail' : 'editSupportEmail')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {provider.company_name}
                        </Typography>
                    </DialogTitle>

                    <DialogContent dividers sx={{ flex: '1 1 auto', overflowY: 'auto' }}>
                        <FormGridLayout gap={2} sx={{ gridTemplateColumns: '1fr' }}>
                            <FormField
                                name="support_email"
                                label="supportEmail"
                                type="email"
                                disabled={loading}
                            />
                            <FormField
                                name="support_email_password"
                                label="supportEmailPassword"
                                type="password"
                                disabled={loading}
                            />
                            <FormHelperText>
                                {translate(
                                    isNew
                                        ? 'supportEmailPasswordRequired'
                                        : 'supportEmailPasswordHint'
                                )}
                            </FormHelperText>
                        </FormGridLayout>
                    </DialogContent>

                    <DialogActions sx={{ flexShrink: 0, px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={loading}
                            sx={{ textTransform: 'none' }}
                        >
                            {translate('cancel')}
                        </Button>
                        <SubmitButton loading={loading} sx={{ textTransform: 'none' }}>
                            save
                        </SubmitButton>
                    </DialogActions>
                </Form>
            </Formik>
        </Dialog>
    );
}

export default SupportEmailForm;
